<template>
    <div class="dynmap">
        <!-- 工具条：图例 + 操作 -->
        <div class="map-toolbar">
            <div class="legend">
                <span class="legend-item"><i class="dot dot-cur"></i>当前位置</span>
                <span class="legend-item"><i class="badge badge-deliver">交</i>待交付</span>
                <span class="legend-item"><i class="badge badge-target">目</i>任务目标</span>
                <span class="legend-item"><i class="badge badge-accept">接</i>可接任务</span>
                <span class="legend-item"><i class="dot dot-event"></i>特殊事件</span>
            </div>
            <div class="actions">
                <div class="zoom-ctrl">
                    <el-button size="mini" icon="el-icon-minus" :disabled="zoom <= minZoom" @click="zoomOut"></el-button>
                    <span class="zoom-label">{{ Math.round(zoom * 100) }}%</span>
                    <el-button size="mini" icon="el-icon-plus" :disabled="zoom >= maxZoom" @click="zoomIn"></el-button>
                    <el-button size="mini" v-if="zoom !== 1" @click="resetZoom">重置</el-button>
                </div>
                <el-button size="mini" icon="el-icon-refresh" :loading="loading" @click="refreshNow">刷新</el-button>
                <el-button size="mini" @click="showBigMap">查看原图</el-button>
            </div>
        </div>

        <!-- 地图画布：横向滚动容器 -->
        <div class="map-scroll" ref="mapScroll">
            <div class="map-canvas" v-loading="loading" :style="canvasStyle">
                <div class="map-layer" :style="layerStyle">
                    <svg class="edges" :width="baseW" :height="baseH">
                        <line v-for="e in edges" :key="e.key" :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2" vector-effect="non-scaling-stroke" />
                    </svg>
                    <div v-for="cell in cells" :key="cell.place.id"
                     class="cell" :class="cellClass(cell)"
                     :style="cellStyle(cell)"
                     :title="cell.place.placename"
                     @click="showDetail(cell)">
                    <div class="cell-name">{{ cell.place.placename }}</div>
                    <div class="cell-icons">
                        <span v-if="cell.isCur" class="badge badge-cur">我</span>
                        <span v-if="cell.isDeliver" class="badge badge-deliver">交</span>
                        <span v-if="cell.isTarget" class="badge badge-target">目</span>
                        <span v-if="cell.isAccept" class="badge badge-accept">接</span>
                    </div>
                    <span v-if="cell.eventCount" class="event-dot" :title="cell.eventNames">{{ cell.eventCount }}</span>
                </div>
                </div>
            </div>
        </div>

        <!-- 地点详情弹窗 -->
        <el-dialog :title="detailTitle" :visible.sync="dialogVisible" append-to-body width="480px" custom-class="map-detail-dialog">
            <div v-if="detail" class="detail-body">
                <p class="detail-desc">{{ detail.place.placedesc || '暂无描述' }}</p>
                <div v-if="detail.events.length" class="detail-section">
                    <div class="section-title">特殊事件</div>
                    <el-tag v-for="ev in detail.events" :key="ev.id" size="small" class="event-tag">{{ ev.eventName }}</el-tag>
                </div>
                <div v-if="detail.tasks.length" class="detail-section">
                    <div class="section-title">我在这里的任务</div>
                    <div v-for="t in detail.tasks" :key="t.id" class="task-line">
                        <el-tag size="mini" :type="t.taskStatus == 1 ? 'danger' : 'warning'">{{ t.taskStatus == 1 ? '待交付' : '进行中' }}</el-tag>
                        {{ t.taskName }}
                        <span class="task-npc">（{{ t.taskStatus == 1 ? '找' + npcName(t.taskFrom) : '目标 ' + npcName(t.taskTo) }}）</span>
                    </div>
                </div>
                <div v-if="detail.npcs.length" class="detail-section">
                    <div class="section-title">这里的NPC（{{ detail.npcs.length }}）</div>
                    <div v-for="npc in detail.npcs" :key="npc.npcId" class="npc-line">
                        <img class="npc-face" :src="ossPath + npc.npcFace" :alt="npc.npcName">
                        <div class="npc-info">
                            <div class="npc-name">
                                {{ npc.npcName }}
                                <el-tag v-if="npc.taskStatus == 1" size="mini" type="danger">待交付</el-tag>
                                <el-tag v-if="isTaskTarget(npc)" size="mini" type="warning">任务目标</el-tag>
                                <el-tag v-if="canAccept(npc)" size="mini" type="primary">可接任务</el-tag>
                            </div>
                            <div class="npc-desc">{{ npc.npcDesc }}</div>
                        </div>
                    </div>
                </div>
                <div v-else class="detail-empty">这里没有NPC</div>
            </div>
        </el-dialog>
    </div>
</template>
<script>
import { postRequest } from '../../utils/api'

// 王都维达尔加动态地图：地点节点按 fantasy_citymap 坐标布局，
// 标记当前位置/待接任务/任务目标/待交付/特殊事件，点击地点查看详情
export default {
    name: 'wangdu',
    // cityId 不传时由后端按玩家当前所在地判定：这样一来同一个组件既能当「王都地图」，
    // 也能当「幻之都地图」，跨城后点开自动显示玩家所在的那座城，不会出现
    // 「人已经在幻之都，弹出来还是王都的图、自己在图上找不到」的情况。
    props: {
        cityId: { type: [String, Number], default: null }
    },
    data() {
        return {
            loading: false,
            places: [],
            npcs: [],
            specialEvents: [],
            myTasks: [],
            curMapId: null,
            city: {},
            dialogVisible: false,
            detail: null,
            isMobile: window.innerWidth <= 768,
            zoom: 1,
            minZoom: 0.5,
            maxZoom: 2,
            mapTimer: null,
            centerTimer: null
        }
    },
    computed: {
        bounds() {
            let minX = 0, maxX = 0, minY = 0, maxY = 0, first = true
            for (const p of this.places) {
                if (p.axisX == null || p.axisY == null) continue
                if (first) { minX = maxX = p.axisX; minY = maxY = p.axisY; first = false; continue }
                minX = Math.min(minX, p.axisX); maxX = Math.max(maxX, p.axisX)
                minY = Math.min(minY, p.axisY); maxY = Math.max(maxY, p.axisY)
            }
            return { minX, maxX, minY, maxY }
        },
        // 网格几何：格子尺寸随端型变化
        cellW() {
            return this.isMobile ? 68 : 92
        },
        cellH() {
            return this.isMobile ? 44 : 52
        },
        baseW() {
            const cols = this.bounds.maxX - this.bounds.minX + 1
            return cols * this.cellW
        },
        baseH() {
            const rows = this.bounds.maxY - this.bounds.minY + 1
            return rows * this.cellH
        },
        canvasStyle() {
            // 外层画布占缩放后的实际尺寸，保证滚动条正确
            return {
                width: Math.round(this.baseW * this.zoom) + 'px',
                height: Math.round(this.baseH * this.zoom) + 'px'
            }
        },
        layerStyle() {
            return {
                width: this.baseW + 'px',
                height: this.baseH + 'px',
                transform: 'scale(' + this.zoom + ')',
                transformOrigin: '0 0',
                backgroundSize: this.cellW + 'px ' + this.cellH + 'px'
            }
        },
        npcById() {
            const map = {}
            for (const n of this.npcs) map[n.npcId] = n
            return map
        },
        // 待交付地点：条件达成(taskStatus=1)的任务，回发布人(taskFrom)处交付
        deliverMapIds() {
            const ids = {}
            for (const t of this.myTasks) {
                if (t.taskStatus == 1 && t.taskFrom && this.npcById[t.taskFrom]) {
                    ids[this.npcById[t.taskFrom].mapId] = true
                }
            }
            return ids
        },
        // 任务目标地点：进行中(taskStatus=0)的任务，找目标NPC(taskTo)
        targetMapIds() {
            const ids = {}
            for (const t of this.myTasks) {
                if (t.taskStatus == 0 && t.taskTo && this.npcById[t.taskTo]) {
                    ids[this.npcById[t.taskTo].mapId] = true
                }
            }
            return ids
        },
        // 可接任务地点：NPC开启了通用/专有任务发放
        acceptMapIds() {
            const ids = {}
            for (const n of this.npcs) {
                if ((n.npcCommonTaskStatus == 1 || n.npcSpecialTaskStatus == 1) && n.mapId) {
                    ids[n.mapId] = true
                }
            }
            return ids
        },
        npcsByMapId() {
            const map = {}
            for (const n of this.npcs) {
                (map[n.mapId] = map[n.mapId] || []).push(n)
            }
            return map
        },
        eventsByMapId() {
            const map = {}
            for (const e of this.specialEvents) {
                (map[e.mapId] = map[e.mapId] || []).push(e)
            }
            return map
        },
        // 有坐标的地点 → 网格单元（含标记位）
        cells() {
            return this.places
                .filter(p => p.axisX != null && p.axisY != null)
                .map(p => {
                    const events = this.eventsByMapId[p.id] || []
                    return {
                        place: p,
                        col: p.axisX - this.bounds.minX,
                        row: this.bounds.maxY - p.axisY,
                        isCur: this.curMapId != null && String(p.id) === String(this.curMapId),
                        isDeliver: !!this.deliverMapIds[p.id],
                        isTarget: !!this.targetMapIds[p.id],
                        isAccept: !!this.acceptMapIds[p.id],
                        eventCount: events.length,
                        eventNames: events.map(e => e.eventName).join('、')
                    }
                })
        },
        // 相邻地点连线：按八方邻接字段去重画边（id 小的在前）
        edges() {
            const DIRS = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest']
            const centers = {}
            for (const p of this.places) {
                if (p.axisX == null || p.axisY == null) continue
                centers[String(p.id)] = {
                    cx: (p.axisX - this.bounds.minX) * this.cellW + this.cellW / 2,
                    cy: (this.bounds.maxY - p.axisY) * this.cellH + this.cellH / 2
                }
            }
            const seen = {}
            const list = []
            for (const p of this.places) {
                if (p.axisX == null || p.axisY == null) continue
                for (const d of DIRS) {
                    const tid = p[d]
                    if (tid == null || tid === '' || tid === '0' || tid === 0) continue
                    const a = String(p.id), b = String(tid)
                    const key = a < b ? a + '-' + b : b + '-' + a
                    if (seen[key]) continue
                    const ca = centers[a], cb = centers[b]
                    if (!ca || !cb) continue
                    seen[key] = true
                    list.push({ key, x1: ca.cx, y1: ca.cy, x2: cb.cx, y2: cb.cy })
                }
            }
            return list
        },
        detailTitle() {
            if (!this.detail) return ''
            const p = this.detail.place
            return (this.city.cityname ? this.city.cityname + '·' : '') + p.placename
        }
    },
    mounted() {
        this.onResize = () => { this.isMobile = window.innerWidth <= 768 }
        window.addEventListener('resize', this.onResize)
        // 滚轮直接缩放（以光标为中心点），空白处拖动平移
        this.onWheel = (e) => {
            e.preventDefault()
            const scroll = this.$refs.mapScroll
            if (!scroll) return
            const oldZoom = this.zoom
            const newZoom = e.deltaY < 0
                ? Math.min(this.maxZoom, Math.round((oldZoom + 0.1) * 10) / 10)
                : Math.max(this.minZoom, Math.round((oldZoom - 0.1) * 10) / 10)
            if (newZoom === oldZoom) return
            const rect = scroll.getBoundingClientRect()
            const cursorX = e.clientX - rect.left
            const cursorY = e.clientY - rect.top
            const px = cursorX + scroll.scrollLeft
            const py = cursorY + scroll.scrollTop
            this.zoom = newZoom
            this.$nextTick(() => {
                // 保持光标下的内容点不漂移
                const ratio = newZoom / oldZoom
                scroll.scrollLeft = px * ratio - cursorX
                scroll.scrollTop = py * ratio - cursorY
            })
        }
        // 空白处按住拖动平移（document 级监听，避免拖出容器失效）
        this.onDragStart = (e) => {
            if (e.button !== 0) return
            if (e.target.closest && e.target.closest('.cell')) return
            const scroll = this.$refs.mapScroll
            if (!scroll || !scroll.contains(e.target)) return
            e.preventDefault()
            this.dragState = {
                x: e.clientX, y: e.clientY,
                sl: scroll.scrollLeft, st: scroll.scrollTop, moved: false
            }
            scroll.classList.add('dragging')
        }
        this.onDragMove = (e) => {
            if (!this.dragState) return
            const scroll = this.$refs.mapScroll
            if (!scroll) return
            const dx = e.clientX - this.dragState.x
            const dy = e.clientY - this.dragState.y
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.dragState.moved = true
            scroll.scrollLeft = this.dragState.sl - dx
            scroll.scrollTop = this.dragState.st - dy
        }
        this.onDragEnd = () => {
            if (!this.dragState) return
            this.dragState = null
            const scroll = this.$refs.mapScroll
            if (scroll) scroll.classList.remove('dragging')
        }
        const scroll = this.$refs.mapScroll
        if (scroll) scroll.addEventListener('wheel', this.onWheel, { passive: false })
        document.addEventListener('mousedown', this.onDragStart)
        document.addEventListener('mousemove', this.onDragMove)
        document.addEventListener('mouseup', this.onDragEnd)
        this.loadData()
        // 位置动态刷新：每 10 秒静默轮询（页面隐藏时跳过），移动后无需手动刷新地图
        this.mapTimer = setInterval(() => {
            if (document.visibilityState !== 'hidden') {
                this.loadData(true)
            }
        }, 10000)
    },
    beforeDestroy() {
        if (this.mapTimer) {
            clearInterval(this.mapTimer)
            this.mapTimer = null
        }
        if (this.centerTimer) {
            clearTimeout(this.centerTimer)
            this.centerTimer = null
        }
        window.removeEventListener('resize', this.onResize)
        const scroll = this.$refs.mapScroll
        if (scroll && this.onWheel) scroll.removeEventListener('wheel', this.onWheel)
        document.removeEventListener('mousedown', this.onDragStart)
        document.removeEventListener('mousemove', this.onDragMove)
        document.removeEventListener('mouseup', this.onDragEnd)
    },
    methods: {
        zoomIn() {
            this.applyZoom(this.zoom + 0.1)
        },
        zoomOut() {
            this.applyZoom(this.zoom - 0.1)
        },
        // 按钮/滚轮缩放共用：以视口中心为锚点，缩放后原中心内容不漂移，玩家不会跳出视野
        applyZoom(z) {
            z = Math.min(this.maxZoom, Math.max(this.minZoom, Math.round(z * 10) / 10))
            if (z === this.zoom) return
            const scroll = this.$refs.mapScroll
            if (!scroll) { this.zoom = z; return }
            const cx = scroll.scrollLeft + scroll.clientWidth / 2
            const cy = scroll.scrollTop + scroll.clientHeight / 2
            const ratio = z / this.zoom
            this.zoom = z
            this.$nextTick(() => {
                scroll.scrollLeft = cx * ratio - scroll.clientWidth / 2
                scroll.scrollTop = cy * ratio - scroll.clientHeight / 2
            })
        },
        resetZoom() {
            this.zoom = 1
            this.$nextTick(() => this.scrollToCur())
        },
    loadData(silent) {
        if (!silent) this.loading = true
        // 后端直接返回聚合数据（非 BaseResultVO 包装），用 resp.status==200 判断
        const prevMapId = this.curMapId
        const params = this.cityId ? { cityId: String(this.cityId) } : {}
        postRequest('/map/cityMap/getCityMapInfo', params).then(resp => {
            if (resp.status == 200 && resp.data) {
                this.places = resp.data.places || []
                this.npcs = resp.data.npcs || []
                this.specialEvents = resp.data.specialEvents || []
                this.myTasks = resp.data.myTasks || []
                this.curMapId = resp.data.curMapId
                this.city = resp.data.city || {}
                // 城市名回传给弹窗做标题（只回传一次，轮询不重复触发）
                if (!this._cityEmitted && this.city.cityname) {
                    this._cityEmitted = true
                    this.$emit('city-loaded', this.city)
                }
                // 位置变化（或首次加载）才自动滚动定位，轮询刷新不打扰当前视角
                if (this.curMapId !== prevMapId) {
                    this.$nextTick(() => this.scrollToCur())
                }
            } else if (!silent) {
                this.$message.error('地图数据加载失败')
            }
        }).catch(() => {
            if (!silent) this.$message.error('网络异常，请稍后重试')
        }).finally(() => {
            this.loading = false
        })
    },
    // 手动刷新（带 loading 提示）
    refreshNow() {
        this.loadData(false)
    },
        cellStyle(cell) {
            return {
                left: cell.col * this.cellW + 'px',
                top: cell.row * this.cellH + 'px',
                width: (this.cellW - 6) + 'px',
                height: (this.cellH - 6) + 'px'
            }
        },
        cellClass(cell) {
            return {
                'cell-cur': cell.isCur,
                'cell-deliver': cell.isDeliver,
                'cell-target': cell.isTarget,
                'cell-accept': cell.isAccept
            }
        },
        // 把节点摆到视口里：能居中就居中；受可滚范围钳制（贴边节点）时，
        // 退而求其次保证它完整落在可视区域内——只要看得见，不要求正中。
        // 返回 true 表示节点已在可视区内（无需再校正）
        centerCell(scroll, cur) {
            const vw = scroll.clientWidth
            const vh = scroll.clientHeight
            if (!vw || !vh) return false
            const cw = this.cellW * this.zoom
            const ch = this.cellH * this.zoom
            const x0 = cur.col * cw
            const y0 = cur.row * ch
            // 1) 先按居中算（越界部分由浏览器按可滚范围钳制）
            scroll.scrollLeft = Math.max(0, x0 + cw / 2 - vw / 2)
            scroll.scrollTop = Math.max(0, y0 + ch / 2 - vh / 2)
            // 2) 校正：若被钳制后节点没完整落在可视区内（贴边、或画布比视口还小），
            //    按最小位移把它拉进来；每个方向只挪一次，避免来回抖动
            const margin = 12
            let dx = 0, dy = 0
            const nx = x0 - scroll.scrollLeft
            const ny = y0 - scroll.scrollTop
            if (nx < margin) dx = nx - margin
            else if (nx + cw > vw - margin) dx = nx + cw - vw + margin
            if (ny < margin) dy = ny - margin
            else if (ny + ch > vh - margin) dy = ny + ch - vh + margin
            if (dx) scroll.scrollLeft += dx
            if (dy) scroll.scrollTop += dy
            // 3) 复查是否可见（不可见说明画布尺寸还没就绪，等下一轮重试）
            const fx = x0 - scroll.scrollLeft
            const fy = y0 - scroll.scrollTop
            return fx >= -1 && fy >= -1 && fx + cw <= vw + 1 && fy + ch <= vh + 1
        },
        scrollToCur() {
            const scroll = this.$refs.mapScroll
            if (!scroll) return
            const cur = this.cells.find(c => c.isCur)
            if (!cur) return
            // 直接赋值（不走 smooth 选项）避免部分移动端内核丢滚动。
            // 首帧画布尺寸未就绪会让滚动被错误钳制，故按 120ms 重试到节点可见为止（上限 1s）
            if (this.centerTimer) { clearTimeout(this.centerTimer); this.centerTimer = null }
            this.centerCell(scroll, cur)
            const deadline = Date.now() + 1000
            const tick = () => {
                this.centerTimer = null
                if (!this.$refs.mapScroll) return
                if (Date.now() > deadline || this.centerCell(scroll, cur)) return
                this.centerTimer = setTimeout(tick, 120)
            }
            this.centerTimer = setTimeout(tick, 120)
        },
        canAccept(npc) {
            return npc.npcCommonTaskStatus == 1 || npc.npcSpecialTaskStatus == 1
        },
        isTaskTarget(npc) {
            return this.myTasks.some(t => t.taskStatus == 0 && String(t.taskTo) === String(npc.npcId))
        },
        npcName(npcId) {
            const npc = this.npcById[npcId]
            return npc ? npc.npcName : '？'
        },
        showDetail(cell) {
            const place = cell.place
            const npcs = this.npcsByMapId[place.id] || []
            // 与该地点相关的我的任务：可交付（发布人在此）或目标（目标NPC在此）
            const tasks = this.myTasks.filter(t => {
                const from = this.npcById[t.taskFrom], to = this.npcById[t.taskTo]
                return (t.taskStatus == 1 && from && String(from.mapId) === String(place.id)) ||
                    (t.taskStatus == 0 && to && String(to.mapId) === String(place.id))
            })
            this.detail = {
                place,
                npcs,
                events: this.eventsByMapId[place.id] || [],
                tasks
            }
            this.dialogVisible = true
        },
        showBigMap() {
            const mask = document.createElement('div')
            mask.className = 'big-map-mask'
            mask.onclick = function () {
                document.body.removeChild(mask)
            }
            const imgContainer = document.createElement('div')
            imgContainer.className = 'big-map-container'
            imgContainer.onclick = function (e) {
                e.stopPropagation()
                document.body.removeChild(mask)
            }
            const img = document.createElement('img')
            img.src = this.ossPath + 'map/wangdu.jpg'
            img.className = 'big-map-img'
            img.onclick = function (e) {
                e.stopPropagation()
            }
            imgContainer.appendChild(img)
            mask.appendChild(imgContainer)
            document.body.appendChild(mask)
        }
    }
}
</script>
<style scoped>
.dynmap {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: #f0f2f5;
}
.map-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid #e6e8eb;
}
.legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 12px;
    color: #606266;
}
.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
}
.dot-cur { background: #67c23a; }
.dot-event { background: #9b59b6; }
.badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    font-size: 10px;
    line-height: 1;
    color: #fff;
    font-style: normal;
}
.badge-cur { background: #67c23a; }
.badge-deliver { background: #f56c6c; }
.badge-target { background: #e6a23c; }
.badge-accept { background: #409eff; }
.actions {
    display: flex;
    align-items: center;
    gap: 6px;
}
.zoom-ctrl {
    display: flex;
    align-items: center;
    gap: 4px;
}
.zoom-ctrl .el-button {
    padding: 7px 9px;
}
.zoom-label {
    font-size: 12px;
    color: #606266;
    width: 42px;
    text-align: center;
}
.map-scroll {
    flex: 1;
    min-height: 0;
    overflow: auto;
    background: #eef1f5;
    position: relative;
    cursor: grab;
    user-select: none;
}
.map-scroll.dragging {
    cursor: grabbing;
}
.map-canvas {
    position: relative;
    margin: 8px;
}
.map-layer {
    position: relative;
    background:
        linear-gradient(rgba(103, 194, 58, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(103, 194, 58, 0.05) 1px, transparent 1px);
}
.edges {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
}
.edges line {
    stroke: #b8bec9;
    stroke-width: 1.5;
    stroke-opacity: 0.85;
}
.cell {
    position: absolute;
    box-sizing: border-box;
    border: 1px solid #c8ccd4;
    border-radius: 6px;
    background: #fff;
    padding: 4px 6px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: box-shadow 0.15s;
    overflow: hidden;
}
.cell:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    z-index: 2;
}
.cell-name {
    font-size: 12px;
    color: #303133;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.cell-icons {
    display: flex;
    gap: 3px;
}
.cell-cur {
    border: 2px solid #67c23a;
    background: #f0f9eb;
    box-shadow: 0 0 6px rgba(103, 194, 58, 0.5);
}
.cell-deliver { border-color: #f56c6c; background: #fef0f0; }
.cell-target { border-color: #e6a23c; background: #fdf6ec; }
.cell-accept { border-style: dashed; border-color: #409eff; }
.event-dot {
    position: absolute;
    left: 4px;
    bottom: 4px;
    min-width: 14px;
    height: 14px;
    border-radius: 7px;
    background: #9b59b6;
    color: #fff;
    font-size: 10px;
    line-height: 14px;
    text-align: center;
    padding: 0 3px;
}
.detail-desc {
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.7;
    color: #606266;
    max-height: 150px;
    overflow-y: auto;
}
.detail-section {
    margin-top: 10px;
}
.section-title {
    font-size: 13px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 6px;
}
.event-tag { margin-right: 6px; margin-bottom: 4px; }
.task-line {
    font-size: 13px;
    color: #303133;
    padding: 3px 0;
}
.task-npc { color: #909399; font-size: 12px; }
.npc-line {
    display: flex;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px dashed #ebeef5;
}
.npc-line:last-child { border-bottom: none; }
.npc-face {
    height: 36px;
    width: auto;
    max-width: 36px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    flex-shrink: 0;
    object-fit: contain;
}
.npc-info { min-width: 0; }
.npc-name {
    font-size: 13px;
    color: #303133;
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}
.npc-desc {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    max-height: 54px;
    overflow: hidden;
}
.detail-empty {
    font-size: 12px;
    color: #909399;
    margin-top: 10px;
}
@media (max-width: 768px) {
    .cell-name { font-size: 11px; }
    .map-toolbar { padding: 6px 8px; }
    .zoom-label { width: 36px; }
}
</style>

<style>
/* 大图遮罩层样式 - 全局样式 */
.big-map-mask {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.85);
    z-index: 600;
    overflow: auto;
    cursor: zoom-out;
}

.big-map-container {
    min-width: 100%;
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
}

.big-map-img {
    width: 80%;
    cursor: default;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

/* 覆盖页面容器样式，居中显示且避免滚动条 */
.iframecontainer {
    margin: 0 auto !important;
}

/* 动态地图详情弹窗：移动端宽度适配 */
@media (max-width: 768px) {
    .map-detail-dialog {
        width: 92% !important;
        margin-top: 8vh !important;
    }
}
</style>
